import api from '../api';

export interface StoreSummary {
  id: string;
  name: string;
  external_id: string | null;
  cyclepay_terminal_id: string | null;
  rom_id: string | null;
  rom_name: string | null;
  dc_id: string | null;
  dc_name: string | null;
}

export interface RomOption {
  id: string;
  name: string;
  level_1_id: string | null;
  dc_name: string | null;
}

export interface PreviewUserRow {
  appUserId: string;
  username: string | null;
  name: string | null;
  role: string | null;
  isActive: boolean;
}

export interface PreviewResponse {
  fingerprint: string;
  blockers: string[];
  oldStore: StoreSummary;
  counts: {
    customers: number;
    simCards: number;
    appUserRoles: number;
  };
  promoDataBalance: {
    balance: number;
    reserved: number;
    debit: number;
    credit: number;
    unit: 'MB';
  };
  users: PreviewUserRow[];
}

export interface ExecuteResponse {
  success: boolean;
  message?: string;
  data?: {
    oldStoreId: string;
    newStoreId: string;
    customersMoved: number;
    simCardsMoved: number;
    userRolesMoved: number;
    promoDataBalanceMoved: number;
    promoDataTransferSkipped: boolean;
    promoDataTransferDescription: string | null;
    newStore: StoreSummary | null;
  };
}

export interface ExecuteOwnershipChangeBody {
  idempotencyKey: string;
  previewFingerprint: string;
  oldStoreId: string;
  newRomId: string | null;
  externalId: string;
  contactEmailAddress?: string;
  newStoreName?: string;
  contactFirstName?: string;
  contactLastName?: string;
}

export const CONFIRM_PHRASE = 'MIGRATE';

export const EXTERNAL_ID_PATTERN = /^[0-9]{7,8}$/;

export function isValidExternalId(externalId: string): boolean {
  return EXTERNAL_ID_PATTERN.test(externalId.trim());
}

export function formatNumber(n: number | null | undefined): string {
  return typeof n === 'number' ? n.toLocaleString() : '—';
}

export function formatMb(n: number | null | undefined): string {
  return typeof n === 'number'
    ? `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}MB`
    : '—';
}

export function summariseStoreLabel(store: StoreSummary): string {
  const parts = [store.name?.trim() || `Store ${store.id}`];
  if (store.external_id) parts.push(`(${store.external_id})`);
  return parts.join(' ');
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  const error = err as {
    response?: {
      data?: {
        message?: string | string[];
        error?: { message?: string | string[] };
      };
    };
    message?: string;
  };
  const message =
    error?.response?.data?.error?.message ??
    error?.response?.data?.message ??
    error?.message;
  return Array.isArray(message) ? message.join('; ') : message || fallback;
}

export async function searchStores(
  searchTerm: string,
): Promise<StoreSummary[]> {
  const trimmed = searchTerm.trim();
  const res = await api.get(
    `/spar-mobile/store-ownership-change/stores?search=${encodeURIComponent(trimmed)}`,
  );
  return (res.data?.data ?? []) as StoreSummary[];
}

export async function fetchRomOptions(): Promise<RomOption[]> {
  const res = await api.get('/spar-mobile/store-ownership-change/roms');
  return (res.data?.data ?? []) as RomOption[];
}

export async function fetchStoreOwnershipPreview(
  storeId: string,
): Promise<PreviewResponse | null> {
  const res = await api.get(
    `/spar-mobile/store-ownership-change/preview/${storeId}`,
  );
  return (res.data?.data ?? null) as PreviewResponse | null;
}

export async function executeStoreOwnershipChange(
  body: ExecuteOwnershipChangeBody,
): Promise<ExecuteResponse['data'] | null> {
  const res = await api.post<ExecuteResponse>(
    '/spar-mobile/store-ownership-change',
    body,
  );
  if (!res.data?.success || !res.data.data) {
    throw new Error(
      res.data?.message ||
        'Ownership change returned no confirmed result. Retry the same request.',
    );
  }
  return res.data.data;
}
